/**
 * The status bar along the top of the window.
 *
 * 🎯 **haugur, 2026-08-11:** *"Create a banner like bar top of windows, same dark
 * transparent background as HUD… we will place items on this bar in the future
 * that could be clickable so the space cannot be part of the game window, so if
 * i click something on the bar it is not interpreted as a click to control view
 * function."*
 *
 * 🔴 **That last sentence is the whole design, and it is structural rather than
 * defensive.** The bar does not "swallow" clicks or check where they landed:
 * **the 3D canvas physically begins below it**, so a click on the bar is not a
 * click on the world in the same way that a click on the browser's own toolbar
 * is not. There is no rule that can be got wrong later, and nothing to remember
 * when the first button is added.
 *
 * ⚠️ **This costs about 34 px of view height**, which is the trade he chose when
 * the alternative — floating the bar over a full-height picture — was offered.
 *
 * ## What it holds, in his order
 *
 * **Time · weather · elevation · ISN93 · who you are.** All five were rows in the
 * HUD until now and were removed from it in the same change; the HUD keeps what
 * is about the *place you are looking at* (street, what you are standing on,
 * what you are aiming at) and the diagnostics.
 *
 * ⚠️ **The whole right-hand side is deliberately empty.** That is where the
 * clickable things go, and leaving it visibly free is the point.
 */

import { t } from '@lofi/shared';

/**
 * How tall the bar is, in CSS pixels.
 *
 * 🔴 **One source of truth, and it is this constant.** The stylesheet reads it
 * through the `--topbar-h` custom property, which this module sets — so the
 * renderer's height, the crosshair's centre and the CSS cannot drift apart. A
 * bar that is 34 px in CSS and 30 px in the camera aspect is a view that is
 * subtly stretched, which nobody would ever diagnose.
 */
export const TOP_BAR_HEIGHT = 34;

/** What the bar shows. Every field may be absent, and shows a dash if it is. */
export interface TopBarReadout {
  /** The world's date and time, already formatted, or `null` before the server says. */
  readonly time: string | null;
  /** The weather in words, or `null` before the server says. */
  readonly weather: string | null;
  /** Metres above the ISN93 datum. */
  readonly elevation: number;
  /** ⚠️ Marked when the flat debug view is on — this is the number that is lying. */
  readonly flat: boolean;
  /** ISN93 easting and northing — a real national-grid coordinate. */
  readonly x: number;
  readonly northing: number;
  /** Who the server says you are, or `null` if nobody. */
  readonly player: string | null;
}

/** The fields, in haugur's order. */
const FIELDS = ['time', 'weather', 'elevation', 'isn93', 'player'] as const;
type Field = (typeof FIELDS)[number];

export class TopBar {
  private readonly root: HTMLDivElement;
  private readonly values = new Map<Field, HTMLElement>();
  private lastRender = 0;

  constructor() {
    /* Set before anything is laid out, so the first frame is already the right
       height rather than resizing once on startup. */
    document.documentElement.style.setProperty('--topbar-h', `${TOP_BAR_HEIGHT}px`);

    this.root = document.createElement('div');
    this.root.className = 'topbar';

    for (const [index, field] of FIELDS.entries()) {
      if (index > 0) {
        const divider = document.createElement('span');
        divider.className = 'topbar-divider';
        /* ⚠️ Decorative, and said so: a screen reader should not read a pipe
           between every reading. */
        divider.setAttribute('aria-hidden', 'true');
        this.root.appendChild(divider);
      }

      const cell = document.createElement('span');
      cell.className = 'topbar-cell';
      const value = document.createElement('b');
      cell.appendChild(value);
      this.root.appendChild(cell);
      this.values.set(field, value);
    }

    document.body.appendChild(this.root);
  }

  /**
   * ⚠️ **Throttled to ~6 Hz and written in place**, exactly like the HUD. These
   * are numbers a person reads, not an animation, and assigning `textContent`
   * dirties layout even when the string has not changed.
   */
  render(r: TopBarReadout): void {
    const now = performance.now();
    if (now - this.lastRender < 160) return;
    this.lastRender = now;

    const unknown = t('common.unknown');

    this.set('time', r.time ?? unknown);
    this.set('weather', r.weather ?? unknown);
    /* Marked here as well as in the banner: this is the number that is lying,
       and it is the one somebody will quote in a bug report. */
    this.set(
      'elevation',
      t(r.flat ? 'topbar.elevationFlat' : 'topbar.elevation', {
        metres: r.elevation.toFixed(1),
      }),
    );
    /* ⚠️ The digits are formatted here rather than by the language file, because
       a grid reference is an identifier and not an amount — thousands separators
       in it would be wrong in every language. */
    this.set(
      'isn93',
      t('topbar.isn93', { easting: r.x.toFixed(0), northing: r.northing.toFixed(0) }),
    );
    /* ⚠️ Words, never an empty space. "Not signed in" is the *reason* you cannot
       build, and a blank would read as the bar being broken. */
    this.set('player', r.player ?? t('topbar.notSignedIn'));
  }

  private set(field: Field, value: string): void {
    const el = this.values.get(field);
    if (el && el.textContent !== value) el.textContent = value;
  }
}
